import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import ConfirmationModal from '../Common/ConfirmationModal';
import newsletterApi from '../../services/newsletterApi.js';
import { EmptyState, LoadingState } from '../Common/PanelState';

const FLARE_RED = '#c4151c';
const FLARE_RED_DARK = '#9f1016';
const Icon = ({ path, className = 'h-5 w-5' }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} /></svg>;

function initStat({ label, value, path, color, right, textColor = '#fff' }) {
  return (
    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: color, color: textColor }}>
          <Icon path={path} className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase text-gray-500 tracking-wide">{label}</div>
          <div className="text-2xl font-extrabold text-gray-900" style={right ? { color: FLARE_RED_DARK } : {}}>{value}</div>
        </div>
      </div>
    </div>
  );
}

export default function FlashNewsletterSection() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [adding, setAdding] = useState(false);
  const [manualForm, setManualForm] = useState({ name: '', email: '', phone: '', source: 'Admin' });
  const [confirmClear, setConfirmClear] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await newsletterApi.list();
      setList(r?.data?.data || r?.data?.subscribers || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load newsletter subscribers');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const emails = new Set(); const phones = new Set();
    list.forEach(s => { if (s.email) emails.add(s.email.toLowerCase()); if (s.phone) phones.add(s.phone); });
    const today = new Date(); today.setHours(0,0,0,0); const ts = today.getTime();
    return {
      total: list.length,
      active: list.filter(s => s.subscribed !== false && s.status !== 'unsubscribed').length,
      today: list.filter(s => new Date(s.created_at || 0).getTime() >= ts).length,
      uniqueEmails: emails.size,
      uniquePhones: phones.size,
    };
  }, [list]);

  const filtered = useMemo(() => {
    let l = list;
    if (statusFilter === 'subscribed') l = l.filter(s => s.subscribed !== false && s.status !== 'unsubscribed');
    if (statusFilter === 'unsubscribed') l = l.filter(s => s.subscribed === false || s.status === 'unsubscribed');
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      l = l.filter(s =>
        (s.name || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q) ||
        (s.phone || '').toLowerCase().includes(q) ||
        (s.source || '').toLowerCase().includes(q)
      );
    }
    return l;
  }, [list, statusFilter, search]);

  const byEmail = useMemo(() => {
    const m = new Map();
    filtered.forEach(s => {
      if (!s.email) return;
      const k = s.email.toLowerCase();
      const arr = m.get(k) || []; arr.push(s); m.set(k, arr);
    });
    return m;
  }, [filtered]);

  const byPhone = useMemo(() => {
    const m = new Map();
    filtered.forEach(s => {
      if (!s.phone) return;
      const k = s.phone;
      const arr = m.get(k) || []; arr.push(s); m.set(k, arr);
    });
    return m;
  }, [filtered]);

  const hasDupes = useMemo(() => {
    for (const v of byEmail.values()) if (v.length > 1) return true;
    for (const v of byPhone.values()) if (v.length > 1) return true;
    return false;
  }, [byEmail, byPhone]);

  const unsubOne = async (s) => {
    await newsletterApi.unsubscribe(s.id);
    toast.success(`${s.name || s.email || s.phone || 'Subscriber'} marked unsubscribed`);
    load();
  };
  const deleteOne = async () => {
    if (!deleting) return;
    await newsletterApi.remove(deleting.id);
    toast.success('Subscriber deleted');
    setDeleting(null);
    load();
  };
  const clearAll = async () => {
    await newsletterApi.clear();
    toast.success('All newsletter subscribers cleared from database');
    setConfirmClear(false);
    load();
  };
  const exportCsv = async () => {
    const n = await newsletterApi.exportCSV();
    toast.success(`Exported ${n} subscribers to CSV`);
  };
  const syncFromSubs = async () => {
    try {
      const r = await newsletterApi.syncSubscribers();
      toast.success(`Synced subscribers (added ${r.added || 0}, updated ${r.updated || 0})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || 'Sync failed');
    }
  };
  const submitManual = async (e) => {
    e.preventDefault();
    if (!manualForm.email) { toast.warn('Email is required'); return; }
    const r = await newsletterApi.subscribe(manualForm);
    if (r.duplicate) toast.warn(`Already subscribed! ${r.record.email || r.record.phone}`);
    else toast.success('Subscriber added');
    setManualForm({ name: '', email: '', phone: '', source: 'Admin' });
    setAdding(false);
    load();
  };

  return (
    <div className="space-y-6">
      <ConfirmationModal isOpen={!!deleting} onClose={() => setDeleting(null)} onConfirm={deleteOne} title="Delete Subscriber">
        Delete subscriber <strong>{deleting?.name || deleting?.email || deleting?.id}</strong>? They'll no longer receive issue alerts or marketing.
      </ConfirmationModal>
      <ConfirmationModal isOpen={confirmClear} onClose={() => setConfirmClear(false)} onConfirm={clearAll} title="Clear All Subscribers">
        Delete ALL newsletter subscribers from the database? This cannot be undone.
      </ConfirmationModal>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="admin-page-title">Flash Newsletter</h1>
          <p className="admin-page-sub">Website newsletter signups (Footer, Contact page, Admin). Duplicates automatically detected by email or phone.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setAdding(a => !a)} className="inline-flex items-center gap-2 px-5 py-2.5 text-white font-bold rounded-lg shadow-lg" style={{ backgroundColor: FLARE_RED }}>
            <Icon path="M12 4v16m8-8H4" className="h-5 w-5" />
            Add Subscriber
          </button>
          <button type="button" onClick={syncFromSubs} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold shadow bg-white border border-gray-200 hover:bg-gray-50 text-gray-700">
            Sync Free/Paid
          </button>
          <button onClick={exportCsv} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold shadow bg-white border border-gray-200 hover:bg-gray-50 text-gray-700">
            <Icon path="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" className="h-5 w-5" />
            Export CSV
          </button>
          <button onClick={() => setConfirmClear(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold bg-red-50 hover:bg-red-100 border border-red-200 text-red-700">
            <Icon path="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a2 2 0 012-2h2a2 2 0 012 2v3" className="h-5 w-5" />
            Clear All
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {initStat({ label: 'Total Subscribers', value: stats.total, path: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', color: FLARE_RED, right: true })}
        {initStat({ label: 'Active', value: stats.active, path: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', color: '#059669' })}
        {initStat({ label: 'Today', value: stats.today, path: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', color: '#7c3aed' })}
        {initStat({ label: 'Unique Emails', value: stats.uniqueEmails, path: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', color: '#2563eb' })}
        {initStat({ label: 'Unique Phones', value: stats.uniquePhones, path: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z', color: '#d97706' })}
      </div>

      {adding && (
        <form onSubmit={submitManual} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
          <h2 className="text-lg font-bold text-gray-800 border-b pb-3">Manually Add Subscriber</h2>
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-700">Name</label>
              <input value={manualForm.name} onChange={(e) => setManualForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Ahmed" className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400" />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">Email</label>
              <input type="email" value={manualForm.email} onChange={(e) => setManualForm(f => ({ ...f, email: e.target.value }))} placeholder="name@domain.com" className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400" />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">Phone / WhatsApp</label>
              <input type="tel" value={manualForm.phone} onChange={(e) => setManualForm(f => ({ ...f, phone: e.target.value }))} placeholder="+92 300 1234567" className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400" />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">Source</label>
              <input value={manualForm.source} onChange={(e) => setManualForm(f => ({ ...f, source: e.target.value }))} placeholder="e.g. Admin, Trade Show" className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button type="button" onClick={() => setAdding(false)} className="px-5 py-2.5 font-semibold rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700">Cancel</button>
            <button type="submit" className="px-6 py-2.5 text-white font-bold rounded-lg shadow-lg" style={{ backgroundColor: FLARE_RED }}>Save Subscriber</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-600">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white">
              <option value="all">All ({list.length})</option>
              <option value="subscribed">Active Subscribed ({stats.active})</option>
              <option value="unsubscribed">Unsubscribed ({stats.total - stats.active})</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Search</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, email, phone, source…" className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400" />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold"
            style={{ backgroundColor: '#fff1f2', color: FLARE_RED_DARK, border: '1px solid rgba(196,21,28,0.15)' }}
          >
            <Icon path="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" className="h-3.5 w-3.5" />
            {filtered.length} matching
          </div>
          {(search.trim() || statusFilter !== 'all') ? (
            <button
              type="button"
              onClick={() => { setSearch(''); setStatusFilter('all'); }}
              className="ml-1 px-2.5 py-1 rounded-md bg-white border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Clear filters
            </button>
          ) : null}
        </div>
      </div>

      {hasDupes && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-amber-500 text-white">
            <Icon path="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-amber-900">Possible duplicate entries detected</div>
            <div className="text-sm text-amber-800 mt-0.5">
              {[...byEmail.values()].filter(v => v.length > 1).length > 0 &&
                `${[...byEmail.values()].filter(v => v.length > 1).length} duplicate email${[...byEmail.values()].filter(v => v.length > 1).length === 1 ? '' : 's'} · `}
              {[...byPhone.values()].filter(v => v.length > 1).length > 0 &&
                `${[...byPhone.values()].filter(v => v.length > 1).length} duplicate phone${[...byPhone.values()].filter(v => v.length > 1).length === 1 ? '' : 's'}`}
              {' '}— new submissions will trigger "Already subscribed" toast instead of creating duplicates.
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
        <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-gray-900">Newsletter list</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {loading ? 'Updating…' : `${filtered.length} ${filtered.length === 1 ? 'subscriber' : 'subscribers'} matching`}
            </div>
          </div>
        </div>
        {loading ? (
          <LoadingState label="Loading newsletter subscribers" />
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No newsletter subscribers yet"
            hint={'When visitors sign up via Footer or Contact page "Flash Newsletter", they automatically appear here with dedupe protection.'}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3 text-left font-semibold">Subscriber</th>
                  <th className="px-6 py-3 text-left font-semibold">Contact</th>
                  <th className="px-6 py-3 text-left font-semibold">Source</th>
                  <th className="px-6 py-3 text-left font-semibold">Signed Up</th>
                  <th className="px-6 py-3 text-left font-semibold">Status</th>
                  <th className="px-6 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((s, idx) => {
                  const dupEmail = byEmail.get(s.email?.toLowerCase?.() || '__noemail__');
                  const dupPhone = byPhone.get(s.phone || '__nophone__');
                  const isDupEmail = s.email && dupEmail && dupEmail.length > 1;
                  const isDupPhone = s.phone && dupPhone && dupPhone.length > 1;
                  return (
                    <tr key={s.id} className={`hover:bg-rose-50/30 ${idx % 2 === 1 ? 'bg-gray-50/60' : 'bg-white'}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shadow-sm" style={{ backgroundColor: FLARE_RED }}>
                            {(s.name || 'S').trim().slice(0, 1).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="font-bold text-gray-900 truncate max-w-[220px]">{s.name || '—'}</div>
                            <div className="text-xs text-gray-400 truncate max-w-[220px]">ID: {s.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {s.email ? <a href={`mailto:${s.email}`} className="block font-semibold text-gray-800 hover:text-red-700 truncate max-w-[240px]">{s.email}</a> : <div className="text-xs text-gray-400 italic">No email</div>}
                          {s.phone ? <a href={`tel:${s.phone}`} className="block text-gray-600 font-semibold">{s.phone}</a> : <div className="text-xs text-gray-400 italic">No phone</div>}
                          {(isDupEmail || isDupPhone) && (
                            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-200 text-[11px] font-black uppercase tracking-widest text-amber-800">
                              <Icon path="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" className="h-3 w-3" />
                              Duplicate
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border bg-indigo-50 text-indigo-800 border-indigo-100">
                          {s.source || 'Website'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {new Date(s.created_at || Date.now()).toLocaleDateString()}
                        <span className="block text-[11px] text-gray-400 font-semibold">
                          {new Date(s.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {(s.subscribed === false || s.status === 'unsubscribed') ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border bg-gray-100 text-gray-700 border-gray-200">Unsubscribed</span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border bg-emerald-50 text-emerald-800 border-emerald-100">Subscribed</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        {s.subscribed !== false && s.status !== 'unsubscribed' ? (
                          <button onClick={() => unsubOne(s)} className="px-3 py-1.5 mr-2 rounded-lg text-sm font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700">Unsubscribe</button>
                        ) : null}
                        <button onClick={() => setDeleting(s)} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-50 hover:bg-red-100 text-red-700 border border-red-200">Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
